/* global axios */
import ApiClient from './ApiClient';

export const buildCompanyParams = (
  page = 1,
  sortAttr = 'name',
  search = ''
) => {
  let params = `page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${encodeURIComponent(search)}`;
  }
  return params;
};

const hasNoteAttachments = note =>
  (note.attachments && note.attachments.length) ||
  (note.attachmentsToRemove && note.attachmentsToRemove.length);

const shouldUseCompanyFormData = company =>
  company?.isFormData || company?.avatar;

const isFileLike = value => {
  if (!value) return false;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  return typeof Blob !== 'undefined' && value instanceof Blob;
};

const companyFormData = company => {
  const formData = new FormData();

  Object.keys(company).forEach(key => {
    if (key === 'isFormData' || company[key] === undefined) return;

    const value = company[key];
    if (value === null) {
      formData.append(`company[${key}]`, '');
    } else if (isFileLike(value)) {
      formData.append(`company[${key}]`, value);
    } else if (Array.isArray(value) || typeof value === 'object') {
      formData.append(`company[${key}]`, JSON.stringify(value));
    } else {
      formData.append(`company[${key}]`, value);
    }
  });

  return formData;
};

const noteFormData = note => {
  const formData = new FormData();
  formData.append('note[content]', note.content || '');

  if (note.attachments) {
    note.attachments.forEach(file => {
      formData.append('note[attachments][]', file);
    });
  }

  if (note.attachmentsToRemove) {
    note.attachmentsToRemove.forEach(attachmentId => {
      formData.append('note[attachments_to_remove][]', attachmentId);
    });
  }

  return formData;
};

class CompanyAPI extends ApiClient {
  constructor() {
    super('companies', { accountScoped: true });
  }

  get(page = 1, sortAttr = 'name') {
    return axios.get(`${this.url}?${buildCompanyParams(page, sortAttr)}`);
  }

  getWallet(page = 1, sortAttr = 'name', scope = 'mine') {
    return axios.get(
      `${this.url}/wallet?${buildCompanyParams(page, sortAttr)}&scope=${scope}`
    );
  }

  search(search = '', page = 1, sortAttr = 'name') {
    return axios.get(
      `${this.url}/search?${buildCompanyParams(page, sortAttr, search)}`
    );
  }

  // perPage e opcional: sem ele o servidor usa a pagina padrao da listagem. A previa da aba
  // Exportar pede uma amostra maior; o controller aplica o proprio teto.
  filter(page = 1, sortAttr = 'name', queryPayload = {}, perPage = null) {
    const params = buildCompanyParams(page, sortAttr);
    const query = perPage ? `${params}&per_page=${perPage}` : params;
    return axios.post(`${this.url}/filter?${query}`, {
      ...queryPayload,
    });
  }

  filterCount(queryPayload = {}) {
    return axios.post(`${this.url}/filter_count`, {
      ...queryPayload,
    });
  }

  importCompanies(payload) {
    const normalizedPayload = payload?.file ? payload : { file: payload };
    const formData = new FormData();
    formData.append('import_file', normalizedPayload.file);
    if (normalizedPayload.labels) {
      normalizedPayload.labels.forEach(label => {
        formData.append('labels[]', label);
      });
    }
    if (normalizedPayload.source) {
      formData.append('source', normalizedPayload.source);
    }
    if (normalizedPayload.schemaVersion) {
      formData.append('schema_version', normalizedPayload.schemaVersion);
    }
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  exportCompanies(queryPayload = {}) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  bulkAssign(params) {
    return axios.post(`${this.url}/bulk_assign`, params);
  }

  bulkUpdate(params) {
    return axios.post(`${this.url}/bulk_update`, params);
  }

  deleteSelectedCompanies(selection) {
    const data = Array.isArray(selection)
      ? { company_ids: selection }
      : selection;
    return axios.delete(`${this.url}/delete_selected`, { data });
  }

  create(company) {
    if (shouldUseCompanyFormData(company)) {
      return axios.post(this.url, companyFormData(company));
    }

    return axios.post(this.url, { company });
  }

  update(id, company) {
    if (shouldUseCompanyFormData(company)) {
      return axios.patch(`${this.url}/${id}`, companyFormData(company));
    }

    return axios.patch(`${this.url}/${id}`, { company });
  }

  destroyAvatar(companyId) {
    return axios.delete(`${this.url}/${companyId}/avatar`);
  }

  destroyCustomAttributes(companyId, customAttributes) {
    return axios.post(`${this.url}/${companyId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  getActivityEvents(companyId, params = {}) {
    return axios.get(`${this.url}/${companyId}/activity_events`, { params });
  }

  getContacts(companyId, params = {}) {
    return axios.get(`${this.url}/${companyId}/contacts`, { params });
  }

  addContact(companyId, params) {
    return axios.post(`${this.url}/${companyId}/contacts`, params);
  }

  removeContact(companyId, associationId) {
    return axios.delete(`${this.url}/${companyId}/contacts/${associationId}`);
  }

  getRelatedCompanies(companyId, params = {}) {
    return axios.get(`${this.url}/${companyId}/related_companies`, { params });
  }

  addRelatedCompany(companyId, params) {
    return axios.post(`${this.url}/${companyId}/related_companies`, params);
  }

  updateRelatedCompany(companyId, relationshipId, params) {
    return axios.patch(
      `${this.url}/${companyId}/related_companies/${relationshipId}`,
      params
    );
  }

  removeRelatedCompany(companyId, relationshipId) {
    return axios.delete(
      `${this.url}/${companyId}/related_companies/${relationshipId}`
    );
  }

  getNotes(companyId, params = {}) {
    return axios.get(`${this.url}/${companyId}/notes`, { params });
  }

  createNote(companyId, note) {
    if (hasNoteAttachments(note)) {
      return axios.post(`${this.url}/${companyId}/notes`, noteFormData(note));
    }

    return axios.post(`${this.url}/${companyId}/notes`, { note });
  }

  updateNote(companyId, noteId, note) {
    if (hasNoteAttachments(note)) {
      return axios.patch(
        `${this.url}/${companyId}/notes/${noteId}`,
        noteFormData(note)
      );
    }

    return axios.patch(`${this.url}/${companyId}/notes/${noteId}`, { note });
  }

  deleteNote(companyId, noteId) {
    return axios.delete(`${this.url}/${companyId}/notes/${noteId}`);
  }

  getTasks(companyId, params = {}) {
    return axios.get(`${this.baseUrl()}/tasks`, {
      params: {
        relatable_type: 'Company',
        relatable_id: companyId,
        ...params,
      },
    });
  }

  createTask(companyId, task) {
    return axios.post(`${this.baseUrl()}/tasks`, {
      task: {
        ...task,
        relatable_type: 'Company',
        relatable_id: companyId,
      },
    });
  }

  updateTask(taskId, task) {
    return axios.patch(`${this.baseUrl()}/tasks/${taskId}`, { task });
  }

  getCompanyLabels(companyId) {
    return axios.get(`${this.url}/${companyId}/labels`);
  }

  updateCompanyLabels(companyId, labels) {
    return axios.post(`${this.url}/${companyId}/labels`, { labels });
  }
}

export default new CompanyAPI();
